import { apiFetch } from "@/api/client";

export type BlogStatus = "draft" | "published";

export type BlogPost = {
  _id: string;
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImageUrl?: string;
  galleryImageUrls?: string[];
  status: BlogStatus;
  source?: "manual" | "ai";
  reviewState?:
    | "none"
    | "pending"
    | "validated"
    | "declined"
    | "auto_published";
  autoPublishAt?: string | null;
  reviewedAt?: string | null;
  reviewAction?: string;
  generationMeta?: {
    provider?: string;
    model?: string;
    targetWordCount?: number;
    actualWordCount?: number;
    generatedAt?: string;
    generationDayKey?: string;
    triggeredBy?: "cron" | "manual";
  };
  publishedAt?: string | null;
  tags?: string[];
  authorId?: string;
  authorName?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Révision éditoriale réelle ; alimente `dateModified`. */
  lastReviewedAt?: string;
};

export type BlogListResult = {
  items: BlogPost[];
  total: number;
  page: number;
  limit: number;
};

type FetchBlogsOptions = {
  page?: number;
  limit?: number;
  status?: "published" | "draft" | "all";
  reviewState?:
    | "all"
    | "none"
    | "pending"
    | "validated"
    | "declined"
    | "auto_published";
  source?: "manual" | "ai";
  tag?: string;
};

export async function fetchBlogs(options?: FetchBlogsOptions) {
  const params = new URLSearchParams();

  if (options?.page) params.set("page", String(options.page));
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.status) params.set("status", options.status);
  if (options?.reviewState) params.set("reviewState", options.reviewState);
  if (options?.source) params.set("source", options.source);
  if (options?.tag) params.set("tag", options.tag);
  const query = params.toString();

  return apiFetch<BlogListResult>(`/api/blogs${query ? `?${query}` : ""}`);
}

export async function fetchBlogBySlug(slug: string) {
  return apiFetch<BlogPost>(`/api/blogs/${slug}`);
}

type BlogPayload = {
  title: string;
  content: string;
  excerpt?: string;
  coverImageUrl?: string;
  galleryImageUrls?: string[];
  status?: BlogStatus;
  tags?: string[];
};

export async function createBlog(payload: BlogPayload) {
  return apiFetch<BlogPost>("/api/blogs", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateBlog(id: string, payload: BlogPayload) {
  return apiFetch<BlogPost>(`/api/blogs/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteBlog(id: string) {
  return apiFetch<void>(`/api/blogs/${id}`, {
    method: "DELETE",
  });
}

export async function publishBlog(id: string) {
  return apiFetch<BlogPost>(`/api/blogs/${id}/publish`, {
    method: "PATCH",
  });
}
